import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchEquity } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  Cell,
  LabelList,
} from "recharts";
import {
  ArrowLeft,
  TrendingUp,
  Users,
  Gem,
  PieChart,
  Loader2,
  ArrowUpRight,
} from "lucide-react";

const fmtInt = (n) => Number(n || 0).toLocaleString();

export default function EquityHub() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEquity()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const issuedPct = data?.authorized
    ? Math.min(100, (data.issued / data.authorized) * 100)
    : 0;

  return (
    <div className="px-4 pt-6 pb-24 sm:pb-12" data-testid="equity-hub-page">
      <button
        type="button"
        onClick={() => navigate("/app/profile")}
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-4"
        data-testid="equity-back"
      >
        <ArrowLeft size={16} /> Back to dashboard
      </button>

      <div className="flex items-center gap-2 mb-1">
        <Gem className="text-violet-600" size={22} />
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
          Equity Hub
        </h1>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        Your stake in the company and how the business is growing.
      </p>

      {loading ? (
        <div className="py-16 flex flex-col items-center text-zinc-400">
          <Loader2 className="animate-spin mb-2" size={22} />
          <span className="text-xs">Loading equity data…</span>
        </div>
      ) : !data ? (
        <div className="py-12 text-center text-sm text-zinc-500">
          Couldn&apos;t load equity data right now.
        </div>
      ) : (
        <div className="space-y-5">
          {/* Two headline boxes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card
              className="rounded-2xl overflow-hidden border-0 bg-gradient-to-br from-violet-700 via-violet-600 to-indigo-600 p-5 text-white"
              data-testid="equity-valuation"
            >
              <div className="flex items-center gap-2 text-white/70 text-[11px] uppercase tracking-widest font-semibold">
                <TrendingUp size={14} /> Current valuation
              </div>
              <div className="mt-2 text-3xl font-extrabold tracking-tight">
                ${fmtInt(data.valuation)}
              </div>
              <div className="text-xs text-white/70 mt-1">Company valuation</div>
            </Card>

            <Card
              className="rounded-2xl border-zinc-200 p-5"
              data-testid="equity-shareholders"
            >
              <div className="flex items-center gap-2 text-zinc-400 text-[11px] uppercase tracking-widest font-semibold">
                <Users size={14} /> Total shareholders
              </div>
              <div className="mt-2 text-3xl font-extrabold tracking-tight text-zinc-900">
                {fmtInt(data.shareholders)}
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                Holders with issued shares
              </div>
            </Card>
          </div>

          {/* Issued vs authorized */}
          <Card className="rounded-2xl border-zinc-200 p-5" data-testid="equity-shares">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-zinc-700 font-semibold text-sm">
                <PieChart size={16} className="text-cyan-600" /> Shares issued
              </div>
              <div className="text-xs text-zinc-500">
                {issuedPct.toFixed(2)}% of authorized
              </div>
            </div>
            <div className="flex items-end justify-between mb-2">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-zinc-400 font-semibold">
                  Issued
                </div>
                <div className="text-2xl font-bold text-zinc-900">
                  {fmtInt(Math.round(data.issued))}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wide text-zinc-400 font-semibold">
                  Authorized
                </div>
                <div className="text-2xl font-bold text-zinc-400">
                  {fmtInt(data.authorized)}
                </div>
              </div>
            </div>
            <div className="h-3 rounded-full bg-zinc-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-500 transition-all"
                style={{ width: `${Math.max(1, issuedPct)}%` }}
              />
            </div>
          </Card>

          {/* Peptide sales volume growth (percentage only) */}
          <Card className="rounded-2xl border-zinc-200 p-5" data-testid="equity-growth">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 text-zinc-700 font-semibold text-sm">
                <TrendingUp size={16} className="text-emerald-600" /> Peptide sales
                volume
              </div>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                <ArrowUpRight size={13} /> Up {data.growth_pct}%
              </span>
            </div>
            <p className="text-xs text-zinc-500 mb-3">
              Relative sales volume, 2025 → 2026 (indexed, no dollar figures shown).
            </p>
            <div className="h-56" data-testid="equity-growth-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data.sales_growth}
                  margin={{ top: 24, right: 8, left: 8, bottom: 4 }}
                >
                  <XAxis
                    dataKey="year"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#71717a", fontSize: 13, fontWeight: 600 }}
                  />
                  <Bar dataKey="pct" radius={[10, 10, 0, 0]} barSize={64}>
                    <Cell fill="#c4b5fd" />
                    <Cell fill="#7c3aed" />
                    <LabelList
                      dataKey="pct"
                      position="top"
                      formatter={(v) => `${v}%`}
                      style={{ fill: "#3f3f46", fontWeight: 700, fontSize: 13 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
